import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { JSONContent } from "@tiptap/core/dist/types";
import { SimpleEditor } from "../@/components/tiptap-templates/simple/simple-editor";
import EmailVariablePicker, {
  VariableNode,
} from "../components/email/EmailVariablePicker";
import { EmailVariable, VariableContext } from "../types/workflow";
import { workflowService } from "../services/workflowService";

// Define the user states for the Update detail page
export type UpdateStatus = "edit" | "preview" | "prepare" | "sent";

// Enhanced interface with workflow integration
interface EmailUpdate {
  id: string;
  title: string;
  body: JSONContent;
  status: UpdateStatus;
  lastSentAt?: string | null;
  workflow_id?: string;
  variables?: Record<string, any>;
}

// Mock workflow context
const mockVariableContext: VariableContext = {
  user: {
    id: "user_123",
    email: "john@example.com",
    name: "John Doe",
    timezone: "America/New_York",
  },
  integrations: {
    jira: {
      connected: true,
      last_sync: "2024-01-15T10:30:00Z",
      data: { bugs_fixed: 12, stories_completed: 8 },
    },
    github: {
      connected: true,
      last_sync: "2024-01-15T10:30:00Z",
      data: { commits_count: 23, pull_requests: 5 },
    },
    asana: {
      connected: true,
      last_sync: "2024-01-15T10:30:00Z",
      data: { tasks_completed: 8, project_progress: 85 },
    },
  },
  workflow: {
    id: "workflow_123",
    name: "Weekly Engineering Report",
    execution_count: 12,
    last_run: "2024-01-15T09:00:00Z",
  },
  custom_variables: {
    "user.name": "John Doe",
    "jira.bugs_fixed": "12",
    "github.commits_count": "23",
    "asana.tasks_completed": "8",
  },
};

// Enhanced dummy data with workflow integration
const enhancedEmailUpdate: EmailUpdate = {
  id: "1",
  title: "Weekly Engineering Update",
  status: "edit",
  lastSentAt: "2024-01-15T12:00:00Z",
  workflow_id: "engineering-metrics-weekly",
  variables: {
    "user.name": "John Doe",
    "jira.bugs_fixed": "12",
    "github.commits_count": "23",
  },
  body: {
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            text: "Hi team! Here's our weekly engineering update for the week ending ",
          },
          {
            type: "variable",
            attrs: {
              variable_id: "current_date",
              variable_name: "system.current_date",
            },
          },
          {
            type: "text",
            text: ".",
          },
        ],
      },
      {
        type: "heading",
        attrs: { level: 2 },
        content: [
          {
            type: "text",
            text: "🐛 Bug Fixes",
          },
        ],
      },
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            text: "This week we resolved ",
          },
          {
            type: "variable",
            attrs: {
              variable_id: "jira_bugs_fixed",
              variable_name: "jira.bugs_fixed",
            },
          },
          {
            type: "text",
            text: " bugs from our Jira backlog. Great work team!",
          },
        ],
      },
      {
        type: "heading",
        attrs: { level: 2 },
        content: [
          {
            type: "text",
            text: "💻 Development Activity",
          },
        ],
      },
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            text: "Our team made ",
          },
          {
            type: "variable",
            attrs: {
              variable_id: "github_commits",
              variable_name: "github.commits_count",
            },
          },
          {
            type: "text",
            text: " commits this week, showing strong development momentum.",
          },
        ],
      },
    ],
  },
};

const UpdateDetailEnhanced: React.FC = () => {
  const { updateId } = useParams<{ updateId: string }>();
  const navigate = useNavigate();
  const [update, setUpdate] = useState<EmailUpdate | null>(null);
  const [loading, setLoading] = useState(true);
  const [editedBody, setEditedBody] = useState<JSONContent | null>(null);
  const [showVariablePicker, setShowVariablePicker] = useState(false);
  const [variablePickerPosition, setVariablePickerPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [availableVariables, setAvailableVariables] = useState<EmailVariable[]>(
    []
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>("");

  useEffect(() => {
    const fetchUpdate = async () => {
      setLoading(true);
      // Simulate API fetch
      await new Promise((r) => setTimeout(r, 500));

      if (updateId === enhancedEmailUpdate.id) {
        setUpdate(enhancedEmailUpdate);
        setEditedBody(enhancedEmailUpdate.body);

        // Load available variables (mock data)
        const mockVariables: EmailVariable[] = [
          {
            id: "current_date",
            name: "system.current_date",
            label: "Current Date",
            type: "date",
            source: "calculation",
            category: "custom",
            description: "Current date when email is sent",
            example_value: "January 15, 2024",
            is_dynamic: true,
          },
          {
            id: "jira_bugs_fixed",
            name: "jira.bugs_fixed",
            label: "Bugs Fixed This Week",
            type: "number",
            source: "integration",
            category: "metrics",
            description: "Number of bugs resolved in Jira",
            example_value: "12",
            integration_source: "jira",
            is_dynamic: true,
          },
          {
            id: "github_commits",
            name: "github.commits_count",
            label: "GitHub Commits",
            type: "number",
            source: "integration",
            category: "metrics",
            description: "Total commits made this week",
            example_value: "23",
            integration_source: "github",
            is_dynamic: true,
          },
        ];
        setAvailableVariables(mockVariables);
      } else {
        navigate("/email-updates");
      }
      setLoading(false);
    };
    fetchUpdate();
  }, [updateId, navigate]);

  const handleVariableInsert = (variable: EmailVariable) => {
    // For now, we'll show instructions to manually insert the variable
    // In a full implementation, we'd need to extend the SimpleEditor to support refs
    const variableText = `{{${variable.name}}}`;

    // Copy to clipboard for easy insertion
    navigator.clipboard
      .writeText(variableText)
      .then(() => {
        alert(
          `Variable "${variable.label}" copied to clipboard!\nPaste ${variableText} into your editor where you want the dynamic content.`
        );
      })
      .catch(() => {
        alert(
          `To insert the variable "${variable.label}", copy this text into your editor: ${variableText}`
        );
      });

    setShowVariablePicker(false);
  };

  const handleInsertVariableClick = (event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setVariablePickerPosition({
      x: rect.left,
      y: rect.bottom + 8,
    });
    setShowVariablePicker(true);
  };

  const handleProcessWithAI = async () => {
    if (!update) return;

    setIsProcessing(true);
    setProcessingStatus("Analyzing content...");

    try {
      // Simulate AI processing workflow
      await new Promise((r) => setTimeout(r, 1000));
      setProcessingStatus("Gathering data from integrations...");

      await new Promise((r) => setTimeout(r, 1500));
      setProcessingStatus("Generating insights...");

      await new Promise((r) => setTimeout(r, 1000));
      setProcessingStatus("Enhancing content...");

      // Simulate workflow execution
      const task = {
        id: `task_${Date.now()}`,
        type: "analysis" as const,
        complexity: "medium" as const,
        data: {
          email_content: editedBody,
          variables: update.variables,
          context: mockVariableContext,
        },
        requirements: {
          aiProcessing: true,
          externalIntegration: true,
          dataVolume: "medium" as const,
        },
        priority: "medium" as const,
      };

      const result = await workflowService.processTask(task);

      if (result.success) {
        setProcessingStatus("Content enhanced successfully!");
        // In real app, would update the editor with enhanced content
        setTimeout(() => {
          setIsProcessing(false);
          setProcessingStatus("");
        }, 1000);
      } else {
        throw new Error(result.error || "Processing failed");
      }
    } catch (error) {
      console.error("AI processing error:", error);
      setProcessingStatus("Processing failed. Please try again.");
      setTimeout(() => {
        setIsProcessing(false);
        setProcessingStatus("");
      }, 2000);
    }
  };

  const onChangeBody = (newBody: JSONContent) => {
    setEditedBody(newBody);
  };

  if (loading || !update) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <main className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {update.title}
            </h1>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  update.status === "edit"
                    ? "bg-yellow-100 text-yellow-800"
                    : update.status === "preview"
                    ? "bg-blue-100 text-blue-800"
                    : update.status === "prepare"
                    ? "bg-purple-100 text-purple-800"
                    : "bg-green-100 text-green-800"
                }`}
              >
                {update.status.charAt(0).toUpperCase() + update.status.slice(1)}
              </span>
              {update.workflow_id && (
                <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">
                  🔄 Automated Workflow
                </span>
              )}
            </div>
          </div>

          {/* Workflow Actions */}
          <div className="flex items-center space-x-3">
            <button
              onClick={handleInsertVariableClick}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <span>🔄</span>
              <span>Insert Variable</span>
            </button>

            <button
              onClick={handleProcessWithAI}
              disabled={isProcessing}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <span>🤖</span>
                  <span>Enhance with AI</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Processing Status */}
        {processingStatus && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center space-x-2">
              <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-sm text-blue-700">{processingStatus}</span>
            </div>
          </div>
        )}
      </div>

      {/* Variable Context Panel */}
      {update.workflow_id && (
        <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            📊 Live Data Context
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(mockVariableContext.integrations).map(
              ([integration, data]) => (
                <div
                  key={integration}
                  className="bg-white p-3 rounded-md border"
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        data.connected ? "bg-green-400" : "bg-red-400"
                      }`}
                    ></div>
                    <span className="text-sm font-medium text-gray-900 capitalize">
                      {integration}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Last sync: {new Date(data.last_sync).toLocaleString()}
                  </div>
                  {data.data && (
                    <div className="mt-2 space-y-1">
                      {Object.entries(data.data).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-xs">
                          <span className="text-gray-600">
                            {key.replace("_", " ")}:
                          </span>
                          <span className="font-medium text-gray-900">
                            {String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* Editor */}
      <div className="mb-6">
        {(update.status === "edit" || update.status === "prepare") && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Email Content
                </span>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <span>💡 Use variables for dynamic content</span>
                  <span>•</span>
                  <span>🤖 AI enhancement available</span>
                </div>
              </div>
            </div>
            <div className="p-4">
              <SimpleEditor
                initialContent={editedBody || { type: "doc", content: [] }}
                onChange={onChangeBody}
              />
            </div>
          </div>
        )}

        {update.status === "preview" && (
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700">
                Email Preview
              </span>
            </div>
            <div className="p-6 prose max-w-none">
              {/* Render preview with processed variables */}
              <EmailPreview
                content={editedBody}
                variables={availableVariables}
                context={mockVariableContext}
              />
            </div>
          </div>
        )}

        {update.status === "sent" && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="text-green-800 font-semibold mb-2">
              ✅ Email sent successfully!
            </div>
            <div className="text-sm text-green-700">
              Sent on{" "}
              {update.lastSentAt
                ? new Date(update.lastSentAt).toLocaleString()
                : "-"}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          className="px-4 py-2 text-gray-600 hover:text-gray-800 flex items-center space-x-2"
          onClick={() => navigate("/email-updates")}
        >
          <span>←</span>
          <span>Back to Updates</span>
        </button>

        <div className="flex space-x-3">
          {(update.status === "edit" || update.status === "prepare") && (
            <button
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={() =>
                alert("Save feature integrated with workflow system")
              }
            >
              💾 Save
            </button>
          )}

          {update.status === "edit" && (
            <button
              className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
              onClick={() =>
                setUpdate((prev) =>
                  prev ? { ...prev, status: "preview" } : null
                )
              }
            >
              👁️ Preview
            </button>
          )}

          {(update.status === "edit" || update.status === "preview") && (
            <button
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              onClick={() =>
                setUpdate((prev) =>
                  prev ? { ...prev, status: "prepare" } : null
                )
              }
            >
              🚀 Prepare
            </button>
          )}

          {update.status === "prepare" && (
            <button
              className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={() => {
                setUpdate((prev) =>
                  prev
                    ? {
                        ...prev,
                        status: "sent",
                        lastSentAt: new Date().toISOString(),
                      }
                    : null
                );
                alert("Email sent via workflow automation!");
              }}
            >
              📨 Send Now
            </button>
          )}
        </div>
      </div>

      {/* Variable Picker Modal */}
      {showVariablePicker && (
        <EmailVariablePicker
          onVariableSelect={handleVariableInsert}
          onClose={() => setShowVariablePicker(false)}
          context={mockVariableContext}
          position={variablePickerPosition || undefined}
        />
      )}
    </main>
  );
};

// Email Preview Component
interface EmailPreviewProps {
  content: JSONContent | null;
  variables: EmailVariable[];
  context: VariableContext;
}

const EmailPreview: React.FC<EmailPreviewProps> = ({
  content,
  variables,
  context,
}) => {
  const renderContent = (node: any): React.ReactNode => {
    if (!node) return null;

    switch (node.type) {
      case "doc":
        return (
          <div>
            {node.content?.map((child: any, index: number) => (
              <div key={index}>{renderContent(child)}</div>
            ))}
          </div>
        );

      case "paragraph":
        return (
          <p className="mb-4">
            {node.content?.map((child: any, index: number) => (
              <span key={index}>{renderContent(child)}</span>
            ))}
          </p>
        );

      case "heading": {
        const HeadingTag = `h${
          node.attrs?.level || 1
        }` as keyof JSX.IntrinsicElements;
        return (
          <HeadingTag className="font-bold mb-3 mt-6">
            {node.content?.map((child: any, index: number) => (
              <span key={index}>{renderContent(child)}</span>
            ))}
          </HeadingTag>
        );
      }

      case "text":
        return node.text;

      case "variable": {
        const variable = variables.find(
          (v) => v.id === node.attrs?.variable_id
        );
        if (variable) {
          const value =
            context.custom_variables?.[variable.name] || variable.example_value;
          return (
            <VariableNode
              variable={variable}
              value={value}
              isEditable={false}
            />
          );
        }
        return `{{${node.attrs?.variable_name || "unknown"}}}`;
      }

      default:
        return null;
    }
  };

  return <div>{renderContent(content)}</div>;
};

export default UpdateDetailEnhanced;
